import React, { useEffect } from "react"
import { Box, Chip, FormHelperText } from "@mui/material"
import { NumberFieldSeeker, ButtonLoadingSeeker } from "@ui-seeker/core"
import { batch, useSignal } from "@preact/signals-react"
import { autoEuropeDrawerData, autoEuropeDrawerErrors } from "Pages/AutoEuropeTool/Page/@signals"
import { durationErrors, overlapsAny, OVERLAP_ERROR } from "Pages/AutoEuropeTool/Page/@utils/durations"

function parseInitialDurations(value) {
	if (!Array.isArray(value) || value.length === 0) return []
	return value
}

function inputErrors(fromVal, toVal) {
	if (fromVal == null || fromVal === "") return { from: "Required", to: null }
	const { from, to } = durationErrors(fromVal, toVal)
	if (from) return { from, to: null }
	if (toVal == null || toVal === "") return { from: null, to: "Required" }
	return { from: null, to }
}

export default function AutoEuropeDurations() {
	const durations = useSignal(parseInitialDurations(autoEuropeDrawerData.peek().durations))
	const fromInput = useSignal("")
	const toInput = useSignal("")
	const isValid = durations.value.length > 0

	useEffect(() => {
		autoEuropeDrawerErrors.value = { ...autoEuropeDrawerErrors.value, durations: isValid }
	}, [isValid])

	const err = inputErrors(fromInput.value, toInput.value)
	// El solape se evalua mientras se escribe, igual que en los provider overrides:
	// asi el boton se apaga y el motivo se ve, en vez de descubrirlo al pulsar Add.
	const overlaps = !err.from && !err.to
		&& overlapsAny(durations.value, Number(fromInput.value), Number(toInput.value))
	const canAdd = !err.from && !err.to && !overlaps

	const add = () => {
		if (!canAdd) return
		batch(() => {
			const updated = [...durations.value, { durationFrom: Number(fromInput.value), durationTo: Number(toInput.value) }]
			durations.value = updated
			autoEuropeDrawerData.value = { ...autoEuropeDrawerData.value, durations: updated }
			fromInput.value = ""
			toInput.value = ""
		})
	}

	const remove = index => batch(() => {
		const updated = durations.value.filter((_, i) => i !== index)
		durations.value = updated
		autoEuropeDrawerData.value = { ...autoEuropeDrawerData.value, durations: updated }
	})

	return <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
		<Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
			<NumberFieldSeeker
				label="Duration From"
				placeholder="1"
				value={fromInput.value}
				onChange={e => fromInput.value = e.target.value}
				error={fromInput.value !== "" ? err.from : undefined}
				sx={{ flex: 1 }}
			/>
			<NumberFieldSeeker
				label="Duration To"
				placeholder="7"
				value={toInput.value}
				onChange={e => toInput.value = e.target.value}
				error={toInput.value !== "" ? err.to : undefined}
				sx={{ flex: 1 }}
			/>
			<ButtonLoadingSeeker
				variant="outlined"
				onClick={add}
				disabled={!canAdd}
				sx={{ mt: 1 }}
			>
				Add
			</ButtonLoadingSeeker>
		</Box>
		{overlaps && <FormHelperText error>{OVERLAP_ERROR}</FormHelperText>}
		{!isValid && <FormHelperText error>Add at least one duration</FormHelperText>}
		{durations.value.length > 0 && (
			<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
				{durations.value.map((d, i) => (
					<Chip
						key={i}
						label={`${d.durationFrom} → ${d.durationTo}`}
						onDelete={() => remove(i)}
						size="small"
					/>
				))}
			</Box>
		)}
	</Box>
}